import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Plus, Trash2, Settings, Loader2 } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const FIELD_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Număr" },
  { value: "date", label: "Dată" },
  { value: "boolean", label: "Da/Nu" },
  { value: "select", label: "Selectare" }
];

const ROLES = [
  { value: "admin", label: "Administrator" },
  { value: "operator", label: "Operator" },
  { value: "agent", label: "Agent" },
  { value: "manager", label: "Manager" }
];

const CustomFields = () => {
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(null);
  
  const [formData, setFormData] = useState({
    key: "",
    label: "",
    field_type: "text",
    required: false,
    options: "",
    applies_to: "contract",
    visible_to_roles: ["admin", "operator", "agent", "manager"]
  });

  const fetchFields = async () => {
    try {
      const response = await axios.get(`${API_URL}/custom-fields`);
      setFields(response.data);
    } catch (error) {
      console.error("Failed to fetch custom fields");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFields();
  }, []);

  const handleCreate = async () => {
    if (!formData.key || !formData.label) {
      toast.error("Cheia și eticheta sunt obligatorii");
      return;
    }
    
    setSaving(true);
    try {
      const payload = {
        ...formData,
        options: formData.field_type === "select" 
          ? formData.options.split(",").map(o => o.trim()).filter(Boolean)
          : null
      };
      
      await axios.post(`${API_URL}/custom-fields`, payload);
      toast.success("Câmp creat cu succes!");
      setDialogOpen(false);
      resetForm();
      fetchFields();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Eroare la crearea câmpului");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    
    try {
      await axios.delete(`${API_URL}/custom-fields/${deleteDialog}`);
      toast.success("Câmp șters");
      setDeleteDialog(null);
      fetchFields();
    } catch (error) {
      toast.error("Eroare la ștergerea câmpului");
    }
  };

  const resetForm = () => {
    setFormData({
      key: "",
      label: "",
      field_type: "text",
      required: false,
      options: "",
      applies_to: "contract",
      visible_to_roles: ["admin", "operator", "agent", "manager"]
    });
  };

  const handleRoleToggle = (role, checked) => {
    if (checked) {
      setFormData(prev => ({
        ...prev,
        visible_to_roles: [...prev.visible_to_roles, role]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        visible_to_roles: prev.visible_to_roles.filter(r => r !== role)
      }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 tracking-tight">Câmpuri Custom</h1>
          <p className="text-slate-500 mt-1">
            Definește câmpuri suplimentare pentru contracte și clienți
          </p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-field-btn">
          <Plus className="h-4 w-4 mr-2" />
          Adaugă Câmp
        </Button>
      </div>

      {/* Fields Table */}
      <Card>
        <CardContent className="p-0">
          {fields.length === 0 ? (
            <div className="text-center py-12">
              <Settings className="h-12 w-12 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">Nu există câmpuri custom</h3>
              <p className="text-slate-500 mb-4">Adaugă primul câmp pentru a extinde modelul de date</p>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Adaugă Câmp
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Cheie</TableHead>
                  <TableHead>Etichetă</TableHead>
                  <TableHead>Tip</TableHead>
                  <TableHead>Obligatoriu</TableHead>
                  <TableHead>Se aplică la</TableHead>
                  <TableHead>Vizibil pentru</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fields.map((field) => (
                  <TableRow key={field.id}>
                    <TableCell className="font-mono text-sm">{field.key}</TableCell>
                    <TableCell>{field.label}</TableCell>
                    <TableCell>
                      <Badge variant="secondary">
                        {FIELD_TYPES.find(t => t.value === field.field_type)?.label || field.field_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {field.required ? (
                        <Badge className="bg-amber-100 text-amber-700">Da</Badge>
                      ) : (
                        <Badge variant="outline">Nu</Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {field.applies_to === "contract" ? "Contract" : "Client"}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {field.visible_to_roles?.slice(0, 2).map(role => (
                          <Badge key={role} variant="secondary" className="text-xs">
                            {ROLES.find(r => r.value === role)?.label || role}
                          </Badge>
                        ))}
                        {field.visible_to_roles?.length > 2 && (
                          <Badge variant="secondary" className="text-xs">
                            +{field.visible_to_roles.length - 2}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => setDeleteDialog(field.id)}
                        data-testid={`delete-${field.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adaugă Câmp Custom</DialogTitle>
            <DialogDescription>
              Definește un câmp suplimentar pentru contracte sau clienți
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Cheie (identificator)</Label>
              <Input
                id="key"
                placeholder="ex: numar_factura"
                value={formData.key}
                onChange={(e) => setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/\s/g, "_") })}
                data-testid="field-key"
              />
              <p className="text-xs text-slate-500">Identificator unic, fără spații</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="label">Etichetă</Label>
              <Input
                id="label"
                placeholder="ex: Număr Factură"
                value={formData.label}
                onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                data-testid="field-label"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Tip Câmp</Label>
              <Select
                value={formData.field_type}
                onValueChange={(value) => setFormData({ ...formData, field_type: value })}
              >
                <SelectTrigger data-testid="field-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {FIELD_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {formData.field_type === "select" && (
              <div className="space-y-2">
                <Label htmlFor="options">Opțiuni</Label>
                <Input
                  id="options"
                  placeholder="Opțiune 1, Opțiune 2, Opțiune 3"
                  value={formData.options}
                  onChange={(e) => setFormData({ ...formData, options: e.target.value })}
                  data-testid="field-options"
                />
                <p className="text-xs text-slate-500">Separă opțiunile cu virgulă</p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Se aplică la</Label>
              <Select
                value={formData.applies_to}
                onValueChange={(value) => setFormData({ ...formData, applies_to: value })}
              >
                <SelectTrigger data-testid="applies-to">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contract">Contract</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox
                id="required"
                checked={formData.required}
                onCheckedChange={(checked) => setFormData({ ...formData, required: !!checked })}
                data-testid="field-required"
              />
              <Label htmlFor="required" className="font-normal">Câmp obligatoriu</Label>
            </div>
            
            <div className="space-y-2">
              <Label>Vizibil pentru rolurile:</Label>
              <div className="grid grid-cols-2 gap-2">
                {ROLES.map((role) => (
                  <div key={role.value} className="flex items-center space-x-2">
                    <Checkbox
                      id={`role-${role.value}`}
                      checked={formData.visible_to_roles.includes(role.value)}
                      onCheckedChange={(checked) => handleRoleToggle(role.value, !!checked)}
                    />
                    <Label htmlFor={`role-${role.value}`} className="font-normal text-sm">
                      {role.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
              Anulează
            </Button>
            <Button onClick={handleCreate} disabled={saving} data-testid="save-field">
              {saving ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Salvează
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={() => setDeleteDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmare Ștergere</DialogTitle>
            <DialogDescription>
              Ești sigur că vrei să ștergi acest câmp? Datele existente pentru acest câmp vor fi pierdute.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(null)}>
              Anulează
            </Button>
            <Button variant="destructive" onClick={handleDelete} data-testid="confirm-delete">
              Șterge
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomFields;
